<template>
    <div>
        <v-dialog v-model="importDialog"
                  persistent width="600px"
                  fullscreen
                  hide-overlay
                  transition="dialog-bottom-transition">
            <v-toolbar dark color="primary">
                <v-toolbar-title>Import de vos prestataires</v-toolbar-title>

                <div class="text-right w-100">
                    <v-btn icon dark @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </v-toolbar>

            <v-stepper v-model="currentStep">
                <v-stepper-header>
                    <v-stepper-step step="1">Sélection fichier</v-stepper-step>

                    <v-divider></v-divider>

                    <v-stepper-step step="2">Vérifications</v-stepper-step>

                    <v-divider></v-divider>

                    <v-stepper-step step="3">Tags</v-stepper-step>
                </v-stepper-header>

                <v-stepper-items>
                    <v-stepper-content step="1">
                        <v-alert text class="mb-5">
                            <p>
                                La liste des prestataires à contacter pendant vos campagnes se définit au moyen d'un export Excel personnalisé généré depuis Tourinsoft.<br />
                                Le format obligatoire du fichier est : <code>id, nom, email, insee, bordereauId</code>
                            </p>

                            <v-expansion-panels >
                                <v-expansion-panel>
                                    <v-expansion-panel-header>Puis-je importer plusieurs fois des prestataires ?</v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <p>
                                            Vous pouvez importer des prestataires autant de fois que nécessaire.<br />
                                            Si vous importez un prestataire déjà existant sur Uska, nous mettrons automatiquement ses données à jour (notamment le destinataire).
                                        </p>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>

                        </v-alert>

                        <div class="d-flex">
                            <v-file-input @change="loadFile"
                                          v-model="file"
                                          accept="application/vnd.ms-excel"
                                          label="Liste des prestataires (.xls)"
                                          outlined
                                          :rules="rules"
                                          dense>
                            </v-file-input>
                        </div>

                        <v-alert v-if="error" type="error">
                            {{ error }}
                        </v-alert>

                        <div class="actions d-flex justify-content-between">
                            <v-btn color="red" style="color: white" @click="closeDialog">Annuler</v-btn>
                            <v-btn class="default-button ml-3 load-button"
                                   :disabled="file === null || !validFile"
                                   @click="loadProviders">
                                Charger <v-icon>keyboard_arrow_right</v-icon>
                            </v-btn>
                        </div>
                    </v-stepper-content>
                </v-stepper-items>

                <v-stepper-content step="2" class="h-100">
                    <v-expansion-panels v-model="panel" mandatory>
                        <v-expansion-panel v-if="providersErrors.length">
                            <v-expansion-panel-header>
                                <div class="table-title">
                                    <v-icon color="warning">warning</v-icon> Fiches en erreur ({{ providersErrors.length }})
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-simple-table height="calc(100vh - 400px)" fixed-header>
                                    <thead>
                                    <tr>
                                        <th class="text-left">Tourinsoft id</th>
                                        <th class="text-left">Nom</th>
                                        <th class="text-left">Catégorie</th>
                                        <th class="text-left">Destinataire</th>
                                        <th class="text-left">Erreur</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="provider in providersErrors" :key="provider.id">
                                        <td>{{ provider.id }}</td>
                                        <td>{{ provider.name }}</td>
                                        <td>{{ provider.type }}</td>
                                        <td>{{ provider.email }}</td>
                                        <td>{{ provider.error.label }}</td>
                                    </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-expansion-panel-content>
                        </v-expansion-panel>

                        <v-expansion-panel v-if="providersToImport.length">
                            <v-expansion-panel-header>
                                <div class="table-title">
                                    <v-icon color="success">check</v-icon> Fiches valides ({{ providersToImport.length }})
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-simple-table height="calc(100vh - 400px)" fixed-header>
                                    <thead>
                                    <tr>
                                        <th class="text-left">Tourinsoft id</th>
                                        <th class="text-left">Nom</th>
                                        <th class="text-left">Catégorie</th>
                                        <th class="text-left">Destinataire</th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="provider in providersToImport" :key="provider.id">
                                        <td>{{ provider.id }}</td>
                                        <td>{{ provider.name }}</td>
                                        <td>{{ provider.type.label }}</td>
                                        <td>{{ provider.email }}</td>
                                        <td>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on }">
                                                    <v-btn v-on="on" icon color="primary" @click="prepareUpdateContact(provider)">
                                                        <v-icon>edit</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Modifier le destinataire</span>
                                            </v-tooltip>
                                        </td>
                                    </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <div class="actions d-flex justify-content-between mt-5 position-absolute"
                         style="bottom: 20px; width: calc(100% - 58px);">

                        <v-btn class="default-button" @click="currentStep = 1">
                            <v-icon>keyboard_arrow_left</v-icon> Sélectionner un fichier
                        </v-btn>
                        <v-btn class="default-button ml-3 load-button"
                               @click="currentStep = 3">
                            Organiser mes {{ providersToImport.length }} fiches <v-icon>keyboard_arrow_right</v-icon>
                        </v-btn>
                    </div>
                </v-stepper-content>

                <v-stepper-content step="3">
                    <v-alert text>
                        <p>
                            Afin de vous faciliter l'organisation ou le tri de vos fiches dans Uska, il est possible dès maintenant de leur associer un <code>tag</code>.
                        </p>
                        <p>
                            Par exemple : vous pourrez plus tard décider d'action spécifiquement dédiées aux fiches taggées <code>adhérents 2020</code>.
                        </p>
                    </v-alert>

                    <v-alert type="info" text>
                        Cette action est <b>optionnelle</b>, vous pourrez taguer vos fiches ultérieurement dans Uska.
                    </v-alert>

                    <v-combobox outlined
                                class="w-50"
                                label="Tags"
                                multiple
                                chips
                                small-chips
                                :items="tags"
                                item-text="name"
                                deletable-chips
                                v-model="selectedTags"
                                return-object>
                    </v-combobox>

                    <div class="actions d-flex justify-content-between mt-5">
                        <v-btn class="default-button" @click="currentStep = 2">
                            <v-icon>keyboard_arrow_left</v-icon> Vérifier mes fiches
                        </v-btn>
                        <v-btn class="default-button ml-3 load-button" @click="importProviders">
                            Importer
                            <span class="ml-1 mr-1" v-if="selectedTags.length">et tager</span>
                            {{ providersToImport.length }} fiches
                        </v-btn>
                    </div>
                </v-stepper-content>
            </v-stepper>
        </v-dialog>

        <update-receiver-modale :edit-contact-modal="editContactModal"
                                :selected-contact="selectedContact"
                                :selected-provider="selectedProvider"
                                :no-db="true">
        </update-receiver-modale>
    </div>
</template>

<script>
    import {mapState} from "vuex"
    import UpdateReceiverModale from "@/components/provider/UpdateReceiverModale"

    export default {
        name: "ImportTourinsoft",
        components: {UpdateReceiverModale},
        props: ['importDialog'],
        data() {
            return {
                currentStep: 1,
                file: null,
                providersToCheck: [],
                providersErrors: [],
                checkedProviders: [],
                notFoundProviders: [],
                unhandledProviders: [],
                providersToImport: [],
                progress: 0,
                selectedTags: [],
                panel: [0],
                error: null,
                rules: [
                    value => !value || value.name.substr(value.name.length - 3) === 'xls' || 'Le fichier doit être au format .xls',
                ],
                validFile: false,
                editContactModal: false,
                selectedContact: null,
                selectedProvider: []
            }
        },
        methods: {
            loadFile(file) {
                if(file !== undefined) {
                    this.file      = file
                    this.validFile = file.name.substr(file.name.length - 3) === 'xls'
                }
            },
            loadProviders() {
                let formData = new FormData
                formData.append('import', this.file)
                formData.append('fieldId', 'id')

                this.$global.loading = true
                this.providersErrors = []
                this.error           = null

                this.$http.post('auth/providers/import-providers', formData).then(res => {
                    this.$global.loading  = false
                    this.providersToCheck = res.body.data.providers
                    this.error            = res.body.data.error

                    if(this.error === null) {
                        this.checkTourinsoftProviders()
                        this.currentStep = 2
                    }
                })
            },
            async checkTourinsoftProviders() {
                this.checkedProviders    = collect()
                this.notFoundProviders   = []
                this.unhandledProviders  = []

                let payload = {
                    providers: this.providersToCheck
                }

                this.$http.post('auth/providers/check-ts-providers', payload).then(res => {
                    let unhandled    = res.body.data.unhandledProviders
                    let providersRes = res.body.data.handledProviders

                    providersRes.forEach(provider => {
                        this.checkedProviders.push(provider)
                    })

                    unhandled.forEach(provider => {
                        provider.error = {
                            code: 1,
                            label: 'Bordereau incompatible'
                        }

                        this.unhandledProviders.push(provider)
                        this.providersErrors.push(provider)
                    })

                    this.providersToImport = this.checkedProviders.all()
                })
            },
            importProviders() {
                setTimeout(() => {
                    let newTags = []
                    let tags    = []

                    this.selectedTags.forEach(tag => {
                        if(typeof tag === "object") {
                            tags.push(tag)
                        } else {
                            newTags.push(tag)
                        }
                    })

                    let payload = {
                        providers: this.providersToImport,
                        newTags: newTags,
                        tags: tags,
                        tisType: 'TOURINSOFT'
                    }

                    this.$global.loading = true

                    this.$http.post('auth/providers/import', payload).then(res => {
                        this.selectedTags = []

                        this.$global.loading = false
                        this.$global.$emit('load-providers')

                        this.$store.dispatch('account/fetchCommunes')
                        this.$store.dispatch('account/fetchCategories')
                        this.$store.dispatch('resource/fetchWebFormStates')

                        this.currentStep = 1
                    })
                }, 500)
            },
            closeDialog() {
                this.$global.$emit('close-import')
                this.currentStep = 1
            },
            prepareUpdateContact(provider) {
                this.editContactModal = true
                this.selectedContact  = provider.email
                this.selectedProvider = provider
            }
        },
        computed: {
            ...mapState('account', ['tags'])
        },
        created() {
            this.$global.$on('close-update-modal', () => {
                this.editContactModal = false
            })

            this.$global.$on('update-email', (email) => {
                this.providersToImport.forEach(provider => {
                    if(provider === this.selectedProvider) {
                        provider.email = email
                    }
                })
            })
        },
        beforeDestroy() {
            this.$global.$off('update-email')
            this.$global.$off('close-update-modal')
        }
    }
</script>
